import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  BookOpen,
  Layers,
  Plus,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import {
  knowledgeListKnowledgeItems,
  knowledgeCreateKnowledgeItem,
} from "../gen";
import type { KnowledgeItemResponse } from "../gen/models";
import { useOrg } from "../hooks/useOrg";
import { useSearchQuery } from "../hooks/useSearchQuery";
import { useFilterParam } from "../hooks/useFilterParam";
import { useToast } from "../context/ToastContext";
import { KnowledgeDetail } from "./KnowledgeDetail";
import { ChunkDetail } from "./ChunkDetail";
import { CreateDocumentModal } from "../components/knowledge/CreateDocumentModal";
import { PaginationControls } from "../components/common/PaginationControls";
import { ScrollableColumnContainer } from "../components/common/ScrollableColumnContainer";
import { FilterSelect, type FilterSelectOption } from "../components/common/FilterSelect";
import { useAgentOptions } from "../hooks/useAgentOptions";
import { TimeAgo, getKnowledgeItemCreator } from "../components/common/TimeAgo";

const KNOWLEDGE_STATUS_OPTIONS: FilterSelectOption[] = [
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
  { value: "archived", label: "Archived" },
];

const KNOWLEDGE_CATEGORY_OPTIONS: FilterSelectOption[] = [
  { value: "logistics", label: "Logistics" },
  { value: "villas", label: "Villas" },
  { value: "policies", label: "Policies" },
  { value: "culinary", label: "Culinary" },
  { value: "activities", label: "Activities" },
  { value: "operations", label: "Operations" },
  { value: "feedback", label: "Feedback" },
];

export function KnowledgeWorkspace() {
  const { item_id, chunk_id } = useParams<{ item_id?: string; chunk_id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { activeOrgId } = useOrg();

  const [items, setItems] = useState<KnowledgeItemResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [agentFilter, setAgentFilter] = useFilterParam("agent", "all", ["agent_type"]);
  const [statusFilter, setStatusFilter] = useFilterParam("status", "all");
  const [categoryFilter, setCategoryFilter] = useFilterParam("category", "all");
  const [searchQuery, setSearchQuery] = useSearchQuery("q");

  const agentOptions = useAgentOptions({ includeUniversal: true });

  // Create Modal state
  const [isCreating, setIsCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newCategory, setNewCategory] = useState("logistics");
  const [newAgentType, setNewAgentType] = useState<string>("universal");
  const [newContent, setNewContent] = useState("");
  const [creatingSubmitting, setCreatingSubmitting] = useState(false);

  const loadItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      };
      if (agentFilter !== "all") params.agent_type = agentFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (searchQuery.trim()) params.q = searchQuery.trim();

      const res = await knowledgeListKnowledgeItems(params);
      const data = (res as any).data;
      if (data && Array.isArray(data.items)) {
        setItems(data.items);
        setTotal(data.total ?? data.items.length);
      } else if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
      } else {
        setItems([]);
        setTotal(0);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load knowledge catalog");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [agentFilter, statusFilter, categoryFilter, searchQuery]);

  useEffect(() => {
    loadItems();
  }, [agentFilter, statusFilter, categoryFilter, searchQuery, page, pageSize, activeOrgId]);

  const handleCreateDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim() || !newContent.trim()) {
      toast.warning("Title and document content are required.");
      return;
    }
    setCreatingSubmitting(true);
    try {
      const res = await knowledgeCreateKnowledgeItem({
        title: newTitle.trim(),
        category: newCategory,
        agent_type: newAgentType === "universal" ? undefined : newAgentType,
        source_type: "manual",
        content: newContent.trim(),
      });
      const created = (res as any).data as KnowledgeItemResponse;
      setIsCreating(false);
      setNewTitle("");
      setNewContent("");
      toast.success("Knowledge document created and indexed successfully.");
      navigate(`/knowledge/${created.id}${location.search}`);
      loadItems();
    } catch (err: any) {
      toast.error("Failed to create document: " + err.message);
    } finally {
      setCreatingSubmitting(false);
    }
  };

  const filteredItems = items;
  const hasSelectedItem = Boolean(item_id && filteredItems.some((it) => it.id === item_id));

  const hasSelection = Boolean(item_id || chunk_id);

  return (
    <div className="flex h-full w-full overflow-hidden min-h-0">
      {/* ============================================================== */}
      {/* COLUMN 2: Compact Knowledge Items Pane                         */}
      {/* ============================================================== */}
      <div
        className={`${
          hasSelection ? "hidden md:flex" : "flex"
        } w-full md:w-80 lg:w-[360px] xl:w-96 shrink-0 h-full border-r border-stone-200 dark:border-stone-800 bg-white/70 dark:bg-stone-900/60 flex-col min-h-0 overflow-hidden select-none transition-all`}
      >
        {/* Pane Header */}
        <div className="p-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between gap-2 shrink-0 bg-stone-50/50 dark:bg-stone-950/50">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-sm font-bold tracking-tight text-stone-900 dark:text-stone-100 truncate">
              Knowledge Docs
            </h2>
            <span className="px-1.5 py-0.2 rounded-md bg-stone-200 dark:bg-stone-800 text-[10px] font-mono font-semibold text-stone-600 dark:text-stone-400">
              {filteredItems.length}
            </span>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={loadItems}
              disabled={loading}
              className="p-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 transition"
              title="Refresh documents list"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              type="button"
              onClick={() => setIsCreating(true)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition shadow-2xs"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>New</span>
            </button>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="p-2.5 border-b border-stone-100 dark:border-stone-800/80 shrink-0 space-y-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400 dark:text-stone-500 pointer-events-none" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-7 py-1.5 text-xs bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 placeholder:text-stone-400 dark:placeholder:text-stone-500 border border-stone-200 dark:border-stone-800 focus:outline-hidden focus:ring-1 focus:ring-stone-400 dark:focus:ring-stone-600 font-sans shadow-2xs"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="grid grid-cols-3 gap-1.5">
            <FilterSelect
              label="Agent"
              value={agentFilter}
              onChange={setAgentFilter}
              options={agentOptions}
            />
            <FilterSelect
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={KNOWLEDGE_STATUS_OPTIONS}
            />
            <FilterSelect
              label="Category"
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={KNOWLEDGE_CATEGORY_OPTIONS}
            />
          </div>
        </div>

        {/* Scrollable Compact List */}
        <ScrollableColumnContainer>
          {loading && items.length === 0 ? (
            <div className="p-8 text-center text-stone-400 dark:text-stone-500 text-xs flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin text-stone-500" />
              <span>Loading documents...</span>
            </div>
          ) : error ? (
            <div className="p-4 rounded-lg bg-rose-50 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-400 text-xs">
              {error}
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="p-8 text-center space-y-2">
              <BookOpen className="w-7 h-7 text-stone-300 dark:text-stone-600 mx-auto" />
              <div className="text-xs font-semibold text-stone-700 dark:text-stone-300">
                No documents found
              </div>
              <p className="text-[11px] text-stone-400 dark:text-stone-500">
                Authoritative hotel information and policies stored here.
              </p>
            </div>
          ) : (
            filteredItems.map((it) => {
              const isSelected = it.id === item_id;
              const isDimmed = hasSelectedItem && !isSelected;

              return (
                <div
                  key={it.id}
                  onClick={() => navigate(`/knowledge/${it.id}${location.search}`)}
                  className={`px-4 py-3 border-b border-l-2 text-xs cursor-pointer transition-all duration-150 space-y-1.5 ${
                    isSelected
                      ? "bg-white dark:bg-stone-900 border-l-stone-900 dark:border-l-stone-100 border-b-stone-200 dark:border-b-stone-800 shadow-2xs opacity-100"
                      : `border-l-transparent border-b-stone-200/80 dark:border-b-stone-800/60 hover:bg-stone-100/70 dark:hover:bg-stone-800/50 ${
                          isDimmed ? "opacity-50 hover:opacity-100" : "opacity-100"
                        }`
                  }`}
                >
                  <div className="flex items-center justify-between gap-1.5">
                    <span className="px-1.5 py-0.2 rounded text-[10px] uppercase font-mono font-bold bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border border-stone-200 dark:border-stone-700">
                      {it.category}
                    </span>

                    <span
                      className={`px-1.5 py-0.2 rounded-full text-[10px] font-semibold capitalize border ${
                        it.status === "active"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-400 dark:border-emerald-800"
                          : "bg-stone-100 text-stone-600 border-stone-200 dark:bg-stone-800 dark:text-stone-400 dark:border-stone-700"
                      }`}
                    >
                      {it.status}
                    </span>
                  </div>

                  <div className="font-semibold text-[11px] text-stone-900 dark:text-stone-100 line-clamp-1">
                    {it.title}
                  </div>

                  <p className="line-clamp-2 text-[11px] text-stone-500 dark:text-stone-400 font-mono">
                    {it.content}
                  </p>

                  <div className="flex items-center justify-between text-[10px] text-stone-400 dark:text-stone-500 pt-1 border-t border-stone-100 dark:border-stone-800/60 gap-1.5">
                    <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                      <span className="inline-flex items-center gap-1 font-mono shrink-0">
                        <Layers className="w-2.5 h-2.5" />
                        <span>{it.chunk_count ?? 1} chunks</span>
                      </span>
                      {getKnowledgeItemCreator(it) && (
                        <>
                          <span>•</span>
                          <span
                            className="truncate text-stone-500 dark:text-stone-400"
                            title={`Created by ${getKnowledgeItemCreator(it)}`}
                          >
                            by {getKnowledgeItemCreator(it)}
                          </span>
                        </>
                      )}
                    </div>
                    <TimeAgo date={it.created_at} className="shrink-0 font-mono" />
                  </div>
                </div>
              );
            })
          )}
        </ScrollableColumnContainer>

        {/* Pagination Controls */}
        <PaginationControls
          compact
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(1);
          }}
          disabled={loading}
        />
      </div>

      {/* ============================================================== */}
      {/* COLUMN 3: Detail Workspace Pane                                */}
      {/* ============================================================== */}
      <div
        className={`${
          hasSelection ? "flex" : "hidden md:flex"
        } flex-1 min-w-0 h-full overflow-y-auto [scrollbar-gutter:stable] flex-col bg-stone-50 dark:bg-stone-950 p-4 md:p-6`}
      >
        {chunk_id ? (
          <ChunkDetail key={chunk_id} />
        ) : item_id ? (
          <KnowledgeDetail key={item_id} />
        ) : (
          <div className="h-full w-full flex flex-col items-center justify-center p-8 text-center select-none space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 flex items-center justify-center shadow-xs">
              <BookOpen className="w-6 h-6 text-stone-400 dark:text-stone-500" />
            </div>
            <div className="space-y-1 max-w-sm">
              <h3 className="text-base font-bold text-stone-900 dark:text-stone-100">
                Select a Knowledge Document
              </h3>
              <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed">
                Choose a document from the catalog to inspect raw text, vector embedding chunks,
                and review diffs across versions.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsCreating(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition shadow-2xs"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Create New Document</span>
            </button>
          </div>
        )}
      </div>

      {/* Create Document Modal */}
      <CreateDocumentModal
        isOpen={isCreating}
        onClose={() => setIsCreating(false)}
        newTitle={newTitle}
        onTitleChange={setNewTitle}
        newCategory={newCategory}
        onCategoryChange={setNewCategory}
        newAgentType={newAgentType}
        onAgentTypeChange={setNewAgentType}
        newContent={newContent}
        onContentChange={setNewContent}
        onSubmit={handleCreateDocument}
        creatingSubmitting={creatingSubmitting}
      />
    </div>
  );
}
